import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, Check, Trash2, X, AlertCircle, User, FileText, Utensils, Activity, Baby, ClipboardList, CheckCheck } from 'lucide-react';
import { axiosInstance } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const NOTIFICATION_ICONS = {
  password_change: User,
  profile_edit: User,
  activity_update: Activity,
  daily_log: FileText,
  nutrition: Utensils,
  child_add: Baby,
  child_edit: Baby,
  registration: ClipboardList,
  development: Activity,
  nutritional_status: Utensils,
  rpa_submitted: ClipboardList,
  rpa_reviewed: Check,
  rpa_rejected: AlertCircle,
  rpa_reminder: BellRing,
  default: AlertCircle
};

const NOTIFICATION_COLORS = {
  password_change: "bg-orange-100 text-orange-600",
  profile_edit: "bg-blue-100 text-blue-600",
  activity_update: "bg-green-100 text-green-600",
  daily_log: "bg-purple-100 text-purple-600",
  nutrition: "bg-yellow-100 text-yellow-600",
  child_add: "bg-pink-100 text-pink-600",
  child_edit: "bg-pink-100 text-pink-600",
  registration: "bg-indigo-100 text-indigo-600",
  development: "bg-teal-100 text-teal-600",
  nutritional_status: "bg-amber-100 text-amber-600",
  rpa_submitted: "bg-violet-100 text-violet-600",
  rpa_reviewed: "bg-emerald-100 text-emerald-600",
  rpa_rejected: "bg-red-100 text-red-600",
  rpa_reminder: "bg-amber-100 text-amber-600",
  default: "bg-gray-100 text-gray-600"
};

export default function AdminNotifications({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Endpoint mengikut role: guru guna /guru/notifications, selainnya /admin/notifications
  const isGuru = user?.role === 'guru';
  const apiBase = isGuru ? '/guru/notifications' : '/admin/notifications';

  const fetchNotifications = useCallback(async () => {
    if (!['admin', 'branch_manager', 'superadmin', 'guru'].includes(user?.role)) return;
    
    try {
      const [notifRes, countRes] = await Promise.all([
        axiosInstance.get(apiBase),
        axiosInstance.get(`${apiBase}/unread-count`)
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user?.role, apiBase]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(`${apiBase}/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Gagal menanda notifikasi');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axiosInstance.delete(`${apiBase}/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notifikasi dipadam');
    } catch (error) {
      toast.error('Gagal memadam notifikasi');
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await Promise.all(
        notifications.filter(n => !n.is_read).map(n => 
          axiosInstance.put(`${apiBase}/${n.id}/read`)
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Semua notifikasi ditanda dibaca');
    } catch (error) {
      toast.error('Gagal menanda notifikasi');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to the relevant module when notification is clicked
  const handleNotificationClick = async (notification) => {
    console.log('[AdminNotifications] Notification clicked:', notification);
    
    // Mark as read first
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Close dropdown
    setShowDropdown(false);
    
    // Navigate based on notification type with deep linking
    const type = notification.type || '';
    const childId = notification.child_id || notification.data?.child_id;
    const data = notification.data || {};
    
    console.log('[AdminNotifications] Type:', type, 'ChildId:', childId, 'Data:', data);
    
    // Build query params for deep linking
    const buildParams = (params) => {
      const filtered = Object.entries(params).filter(([_, v]) => v);
      return filtered.length > 0 ? '?' + new URLSearchParams(filtered).toString() : '';
    };
    
    let targetUrl = '/';
    
    // Activity notifications
    if (type.includes('activity')) {
      const params = buildParams({
        date: data.date,
        child_id: childId,
        activity_id: data.activity_id,
        highlight: data.activity_id
      });
      targetUrl = `/activities${params}`;
    } 
    // Daily log notifications
    else if (type.includes('daily_log')) {
      const params = buildParams({
        date: data.date,
        child_id: childId,
        log_id: data.log_id,
        highlight: data.log_id
      });
      targetUrl = `/daily-logs${params}`;
    } 
    // Nutrition/Menu notifications
    else if (type.includes('nutrition')) {
      const params = buildParams({
        date: data.date,
        child_id: childId,
        menu_id: data.nutrition_id || data.menu_id,
        highlight: data.nutrition_id || data.menu_id
      });
      targetUrl = `/nutrition${params}`;
    } 
    // Child related notifications
    else if (type === 'child_add' || type === 'child_edit') {
      if (data.child_id || childId) {
        targetUrl = `/children/${data.child_id || childId}`;
      } else {
        targetUrl = '/children';
      }
    }
    // Registration notifications
    else if (type === 'registration') {
      targetUrl = '/admin/pending-parents';
    }
    // Development notifications
    else if (type === 'development') {
      if (childId) {
        targetUrl = `/development/${childId}`;
      } else {
        targetUrl = '/development';
      }
    }
    // Nutritional status notifications
    else if (type === 'nutritional_status') {
      if (childId) {
        targetUrl = `/nutritional-status/${childId}`;
      } else {
        targetUrl = '/nutritional-status';
      }
    }
    // Album notifications
    else if (type.includes('album')) {
      if (data.album_id) {
        targetUrl = `/album/${data.album_id}`;
      } else {
        targetUrl = '/album-manage';
      }
    }
    // Profile/Password notifications - go to user management
    else if (type === 'password_change' || type === 'profile_edit') {
      if (data.user_id) {
        targetUrl = `/users?highlight=${data.user_id}`;
      } else {
        targetUrl = '/profile-settings';
      }
    }
    // Default - go to dashboard
    else {
      targetUrl = '/dashboard';
    }
    
    console.log('[AdminNotifications] Navigating to:', targetUrl);
    navigate(targetUrl);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Baru sahaja';
    if (diffMins < 60) return `${diffMins} minit lepas`;
    if (diffHours < 24) return `${diffHours} jam lepas`;
    if (diffDays < 7) return `${diffDays} hari lepas`;
    return date.toLocaleDateString('ms-MY');
  };

  // Show notifications for admin, superadmin, and branch_manager sahaja
  if (!['admin', 'superadmin', 'branch_manager', 'guru'].includes(user?.role)) return null;

  return (
    <div className="relative">
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="admin-notifications-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-6 h-6 text-pink-500 animate-pulse" />
        ) : (
          <Bell className="w-6 h-6 text-gray-600" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - using Portal to render outside sidebar */}
      {showDropdown && createPortal(
        <>
          {/* Backdrop - highest z-index */}
          <div 
            className="fixed inset-0 z-[9998] bg-black/30" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Notification Panel - Modal style, centered */}
          <div className="fixed z-[9999] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[400px] bg-white rounded-2xl shadow-2xl border overflow-hidden"
               style={{ maxHeight: '80vh' }}
               data-testid="admin-notifications-dropdown">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-purple-500 text-white p-3 flex justify-between items-center">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Bell className="w-4 h-4" />
                Notifikasi Admin
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-xs bg-white/20 px-2 py-1 rounded hover:bg-white/30 transition"
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    Baca Semua
                  </button>
                )}
                <button onClick={() => setShowDropdown(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Tiada notifikasi</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const IconComponent = NOTIFICATION_ICONS[notif.type] || NOTIFICATION_ICONS.default;
                  const colorClass = NOTIFICATION_COLORS[notif.type] || NOTIFICATION_COLORS.default;
                  
                  return (
                    <div 
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-4 border-b hover:bg-gray-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-pink-50/50' : ''}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notif.title || notif.message}
                          </p>
                          {notif.title && notif.message !== notif.title && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notif.message}</p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-400">{formatTime(notif.created_at)}</p>
                            <span className="text-xs font-medium text-blue-500 hover:text-blue-700 flex items-center gap-1">
                              Lihat butiran
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                          {!notif.is_read && (
                            <button
                              onClick={() => markAsRead(notif.id)}
                              className="p-1 hover:bg-green-100 rounded text-green-600"
                              title="Tanda dibaca"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notif.id)}
                            className="p-1 hover:bg-red-100 rounded text-red-500"
                            title="Padam"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
